import React, { useState, useCallback } from 'react';
import { toast } from 'react-toastify';
import mainAxios from '../../services/mainAxios';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { Checkbox } from 'primereact/checkbox';

const useDownloadInvoices = ({ getBodyPayload, filterInvoices }) => {
  const { t } = useTranslation('translations');
  const [selectedRows, setSelectedRows] = useState([]);
  const [downloadLoading, setDownloadLoading] = useState(false);

  const downloadSelectedInvoices = useCallback(async () => {
    if (!selectedRows || selectedRows.length === 0) {
      toast.warning(t('invoice.noSelected'), { containerId: 'all_toast' });
      return;
    }
    setDownloadLoading(true);
    try {
      const body = getBodyPayload(selectedRows);

      const res = await mainAxios.post('/endpoint/v2/download-multiple', body, {
        responseType: 'arraybuffer',
      });

      const headers = res.headers || {};
      const contentType = (headers['content-type'] || '').toLowerCase();
      const contentDisposition = headers['content-disposition'] || '';

      const isBinary =
        contentType.includes('application/octet-stream') ||
        contentType.includes('zip') ||
        /filename=.*\.zip/i.test(contentDisposition);

      if (isBinary) {
        let filename = moment().format('DD/MM/YYYY') + '.zip';
        const fnMatch = contentDisposition.match(/filename\*?=(?:UTF-8'')?["']?([^;"']+)/i);
        if (fnMatch && fnMatch[1]) {
          try {
            filename = decodeURIComponent(fnMatch[1]);
          } catch (e) {
            filename = fnMatch[1];
          }
        }

        const blob = new Blob([res.data], { type: contentType || 'application/octet-stream' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
        toast.success(t('invoice.downloadSuccess'), { containerId: 'all_toast' });
        return;
      }

      try {
        const text = new TextDecoder().decode(res.data);
        const json = JSON.parse(text);
        const msg = json?.message || json?.error || t('common.error');
        toast.error(msg, { containerId: 'all_toast' });
      } catch (e) {
        toast.error(t('common.unexpectedResponse'), { containerId: 'all_toast' });
      }
    } catch (err) {
      const errRes = err?.response;
      if (errRes && errRes.data) {
        try {
          const text = new TextDecoder().decode(errRes.data);
          const json = JSON.parse(text);
          toast.error(json?.message || json?.error || t('common.error'), { containerId: 'all_toast' });
        } catch (e) {
          toast.error(t('common.error'), { containerId: 'all_toast' });
        }
      } else {
        toast.error(t('common.error'), { containerId: 'all_toast' });
      }
    } finally {
      setDownloadLoading(false);
    }
  }, [selectedRows, t, getBodyPayload]);

  const downloadCheckboxBody = useCallback(
    (invoice) => {
      if (!filterInvoices(invoice)) return null;
      return (
        <Checkbox
          onChange={() => {
            setSelectedRows((state) => {
              const _state = [...state];
              const idx = _state.findIndex((s) => s?.id === invoice?.id);
              if (idx >= 0) {
                _state.splice(idx, 1);
                return _state;
              }
              _state.push({ id: invoice?.id });
              return _state;
            });
          }}
          checked={!!selectedRows.find((s) => s?.id === invoice?.id)}
        />
      );
    },
    [selectedRows, filterInvoices]
  );

  const downloadMultiFilter = useCallback(
    (invoices) => {
      const selectable = (invoices || []).filter((i) => i?.id && filterInvoices(i));
      if (!selectable.length) return null;

      const handleCheckAll = (e) => {
        if (e.checked) {
          const _selected = selectable.map((inv) => ({
            id: inv?.id,
          }));
          setSelectedRows(_selected);
        } else {
          setSelectedRows([]);
        }
      };

      // Check if all selectable items on current page are selected
      const allCurrentPageSelected = selectable.every((inv) =>
        selectedRows.some((s) => s?.id === inv?.id)
      );

      return (
        <Checkbox
          onChange={handleCheckAll}
          checked={allCurrentPageSelected && selectable.length !== 0}
        />
      );
    },
    [selectedRows, filterInvoices]
  );

  const downloadButtonsRight = selectedRows.length > 0 && (
    <button
      className="btn btn-sm btn-outline-primary rounded-pill ml-2 mr-2"
      onClick={downloadSelectedInvoices}
      disabled={selectedRows.length === 0 || downloadLoading}
    >
      {`${t('invoice.downloadSelected')} (${selectedRows.length})`}
    </button>
  );

  return {
    selectedRows,
    setSelectedRows,
    downloadLoading,
    downloadSelectedInvoices,
    downloadCheckboxBody,
    downloadMultiFilter,
    downloadButtonsRight,
  };
};

export default useDownloadInvoices;
